import React, { useState, useEffect, useCallback } from 'react';
import { Warehouse, Plus, X, Ship, Package, Pencil, Trash2, FileText, Layers, ChevronDown, ChevronUp, Upload } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import PurchaseOrderImportModal from './PurchaseOrderImportModal';
import AttachTransportInvoiceModal from './AttachTransportInvoiceModal';
import { Truck, AlertTriangle, Paperclip, Scale } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StockPage = () => {
  const [coils, setCoils] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [products, setProducts] = useState([]);
  const [productStock, setProductStock] = useState([]);
  // Production logs — shown in the Products tab so historical runs can be
  // backdated when needed (e.g. importing pre-system data).
  const [productionLogs, setProductionLogs] = useState([]);
  const [disp, setDisp] = useState(null);
  const [editLog, setEditLog] = useState(null);       // log currently being edited
  const [returnStock, setReturnStock] = useState(null); // product stock row for "return to coil"
  const [returnQty, setReturnQty] = useState('');
  const [returnError, setReturnError] = useState(null);
  const [returnSaving, setReturnSaving] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [reassignLog, setReassignLog] = useState(null);
  const [reassignCoilId, setReassignCoilId] = useState('');
  const [reassignError, setReassignError] = useState(null);
  const [reassignSaving, setReassignSaving] = useState(false);

  const runAudit = async () => {
    setAuditLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${API_URL}/api/admin/coil-balance-audit`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      if (r.ok) setAuditResult(await r.json());
    } catch (e) {
      console.error('Audit failed:', e);
    } finally {
      setAuditLoading(false);
    }
  };
  const [editLogDate, setEditLogDate] = useState(''); // new date being typed
  const [editLogError, setEditLogError] = useState(null);
  const [editLogSaving, setEditLogSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  // In-app confirm dialog (replaces window.confirm)
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCoil, setEditingCoil] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'in_stock', 'shipping'
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [activeTab, setActiveTab] = useState('coils'); // 'coils' or 'products'
  const [showImportModal, setShowImportModal] = useState(false);
  const [attachShipment, setAttachShipment] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    sheetWidth: '',
    thickness: '',
    weightKg: '',
    cost: '',
    coatingGsm: '100',
    status: 'in_stock',
    orderDate: '',
    arrivalDate: '',
    supplier: '',
    invoiceNumber: '',
    documentUrl: '',
    notes: ''
  });

  // Fetch coils, products and product stock
  const fetchData = useCallback(async () => {
    try {
      const [coilsRes, productsRes, stockRes, shipRes, logsRes, dispRes] = await Promise.all([
        fetch(`${API_URL}/api/coils`),
        fetch(`${API_URL}/api/presets`),
        fetch(`${API_URL}/api/product-stock`),
        fetch(`${API_URL}/api/shipments`),
        fetch(`${API_URL}/api/production-logs`),
        fetch(`${API_URL}/api/settings/display`),
      ]);
      if (coilsRes.ok) setCoils(await coilsRes.json());
      if (productsRes.ok) setProducts(await productsRes.json());
      if (stockRes.ok) setProductStock(await stockRes.json());
      if (shipRes.ok) setShipments(await shipRes.json());
      if (logsRes.ok) setProductionLogs(await logsRes.json());
      if (dispRes.ok) setDisp(await dispRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate sheet width for a product
  const calculateSheetWidth = (product) => {
    const { base, leftSide, rightSide, leftLip, rightLip, thickness } = product;
    const numAngles = 2 + (parseFloat(leftLip) > 0 ? 1 : 0) + (parseFloat(rightLip) > 0 ? 1 : 0);
    const expansion = numAngles * 2 * parseFloat(thickness);
    return parseFloat(base) + parseFloat(leftSide) + parseFloat(rightSide) + 
           (parseFloat(leftLip) || 0) + (parseFloat(rightLip) || 0) - expansion;
  };

  // Calculate profiles per weight
  const calculateProfiles = (weightKg, sheetWidth, thickness, coatingGsm = 100) => {
    // substrate steel + zinc coating (Z100 default): width(m) × 6m × (t×7.85 + gsm/1000)
    const profileWeight = (sheetWidth / 1000) * 6 * (thickness * 7.85 + coatingGsm / 1000);
    if (profileWeight <= 0) return 0;
    // +5g tolerance: stored weights are rounded to 0.01kg, so a reverted run must count back in full
    return Math.floor((weightKg + 0.005) / profileWeight);
  };

  // Get matching products for a dimension
  const getMatchingProducts = (sheetWidth, thickness) => {
    return products.filter(product => {
      const productSheet = calculateSheetWidth(product);
      const productThickness = parseFloat(product.thickness);
      return Math.abs(productSheet - sheetWidth) < 0.5 && 
             Math.abs(productThickness - thickness) < 0.01;
    });
  };

  // Filter coils — "all" = raw coils we have or are coming (never closed ones);
  // closed coils with scrapped weight live in their own "Closed" tab.
  const filteredCoils = coils.filter(coil => {
    const closedWithWeight = coil.status === 'depleted' && (coil.scrappedWeightKg || 0) > 0;
    if (filter === 'closed') return closedWithWeight;
    if (closedWithWeight || (coil.weightKg || 0) <= 0) return false;
    if (filter === 'all') return true;
    return coil.status === filter;
  });

  // Lookup table for shipments — used to compute landed cost & pending status per coil
  const shipmentById = shipments.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});
  const coilLandedInfo = (coil) => {
    const sid = coil.shipmentId;
    const ow = coil.originalWeightKg || coil.weightKg || 0;
    const materialPerKg = (coil.cost && ow > 0) ? coil.cost / ow : 0;
    const ship = sid ? shipmentById[sid] : null;
    const transportPerKg = ship?.transportCostPerKg || 0;
    const pending = ship ? ship.transportPending : true; // un-shipped coils are also "pending"
    return {
      shipment: ship,
      transportPending: pending,
      materialPerKg,
      transportPerKg,
      landedPerKg: materialPerKg + transportPerKg,
    };
  };

  // Group coils by dimension with combined totals
  const groupedCoils = filteredCoils.reduce((acc, coil) => {
    const key = `${coil.sheetWidth}×${coil.thickness}`;
    if (!acc[key]) {
      acc[key] = {
        sheetWidth: coil.sheetWidth,
        thickness: coil.thickness,
        inStockWeight: 0,
        shippingWeight: 0,
        depletedWeight: 0,
        totalCost: 0,
        suppliers: new Set(),
        invoices: new Set(),
        documentUrls: [],
        coils: [],
        pendingShipments: new Set(),
        landedWeightedSum: 0,
        landedWeightedKg: 0,
      };
    }
    acc[key].coils.push(coil);
    if (coil.status === 'in_stock') {
      acc[key].inStockWeight += coil.weightKg;
    } else if (coil.status === 'depleted') {
      acc[key].depletedWeight += (coil.scrappedWeightKg || 0);
    } else {
      acc[key].shippingWeight += coil.weightKg;
    }
    if (coil.cost) acc[key].totalCost += coil.cost;
    if (coil.supplier) acc[key].suppliers.add(coil.supplier);
    if (coil.invoiceNumber) acc[key].invoices.add(coil.invoiceNumber);
    if (coil.documentUrl && !acc[key].documentUrls.includes(coil.documentUrl)) {
      acc[key].documentUrls.push(coil.documentUrl);
    }
    // Per-coil landed cost weighted by originalWeight
    const info = coilLandedInfo(coil);
    if (info.transportPending) {
      acc[key].pendingShipments.add(coil.shipmentId || `orphan-${coil.id}`);
    }
    const ow = coil.originalWeightKg || coil.weightKg || 0;
    if (ow > 0 && info.landedPerKg > 0) {
      acc[key].landedWeightedSum += info.landedPerKg * ow;
      acc[key].landedWeightedKg += ow;
    }
    return acc;
  }, {});

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      sheetWidth: parseFloat(formData.sheetWidth),
      thickness: parseFloat(formData.thickness),
      weightKg: parseFloat(formData.weightKg),
      cost: formData.cost ? parseFloat(formData.cost) : null,
      coatingGsm: formData.coatingGsm ? parseFloat(formData.coatingGsm) : 100,
      status: formData.status,
      orderDate: formData.orderDate || null,
      arrivalDate: formData.arrivalDate || null,
      supplier: formData.supplier || null,
      invoiceNumber: formData.invoiceNumber || null,
      documentUrl: formData.documentUrl || null,
      notes: formData.notes || null
    };

    try {
      if (editingCoil) {
        const response = await fetch(`${API_URL}/api/coils/${editingCoil.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          const updated = await response.json();
          setCoils(prev => prev.map(c => c.id === editingCoil.id ? updated : c));
        }
      } else {
        const response = await fetch(`${API_URL}/api/coils`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          const newCoil = await response.json();
          setCoils(prev => [...prev, newCoil]);
        }
      }
      
      resetForm();
    } catch (error) {
      console.error('Error saving coil:', error);
    }
  };

  // Delete coil
  const handleDelete = async (coilId) => {
    if (!window.confirm('Delete this entry?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/coils/${coilId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setCoils(prev => prev.filter(c => c.id !== coilId));
      }
    } catch (error) {
      console.error('Error deleting coil:', error);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({ 
      sheetWidth: '', thickness: '', weightKg: '', cost: '', coatingGsm: '100',
      status: 'in_stock', orderDate: '', arrivalDate: '', 
      supplier: '', invoiceNumber: '', documentUrl: '', notes: '' 
    });
    setShowAddForm(false);
    setEditingCoil(null);
  };

  // Edit coil
  const startEdit = (coil) => {
    setFormData({
      sheetWidth: coil.sheetWidth.toString(),
      thickness: coil.thickness.toString(),
      weightKg: coil.weightKg.toString(),
      cost: coil.cost?.toString() || '',
      coatingGsm: (coil.coatingGsm ?? 100).toString(),
      status: coil.status,
      orderDate: coil.orderDate || '',
      arrivalDate: coil.arrivalDate || '',
      supplier: coil.supplier || '',
      invoiceNumber: coil.invoiceNumber || '',
      documentUrl: coil.documentUrl || '',
      notes: coil.notes || ''
    });
    setEditingCoil(coil);
    setShowAddForm(true);
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Close out a coil — remaining weight is scrapped, logged against the coil's cost
  const handleCloseOut = (coil) => {
    const kg = coil.weightKg;
    const original = coil.originalWeightKg ?? coil.weightKg;
    const costPerKg = (coil.cost && original) ? coil.cost / original : 0;
    const wasteCost = (kg * costPerKg).toFixed(2);
    setConfirmDialog({
      title: 'Close this coil?',
      description: `${coil.sheetWidth}×${coil.thickness}mm — ${kg.toLocaleString()} kg remaining will be scrapped (≈ €${wasteCost} at €${costPerKg.toFixed(4)}/kg). This cannot be undone.`,
      confirmLabel: 'Close & Scrap',
      confirmVariant: 'destructive',
      onConfirm: async () => {
        try {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${API_URL}/api/coils/${coil.id}/close-out`, true);
          xhr.setRequestHeader('Content-Type', 'application/json');
          const token = localStorage.getItem('auth_token');
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          const done = new Promise((res, rej) => { xhr.onload = () => res({status: xhr.status, body: xhr.responseText}); xhr.onerror = rej; });
          xhr.send(JSON.stringify({}));
          const { status, body } = await done;
          if (status >= 200 && status < 300) {
            await fetchData();
          } else {
            const data = body ? JSON.parse(body) : null;
            alert(data?.detail || `Close-out failed (HTTP ${status})`);
          }
        } catch (e) {
          alert(e.message || 'Network error');
        }
      },
    });
  };

  // Reopen a closed coil — scrapped weight returns to stock, waste logs removed
  const handleReopen = (coil) => {
    const kg = coil.scrappedWeightKg || 0;
    setConfirmDialog({
      title: 'Reopen this coil?',
      description: `${coil.sheetWidth}×${coil.thickness}mm — ${kg.toLocaleString()} kg of scrapped weight returns to stock and the scrap cost is removed.`,
      confirmLabel: 'Reopen',
      onConfirm: async () => {
        try {
          const res = await fetch(`${API_URL}/api/coils/${coil.id}/reopen`, { method: 'POST' });
          if (res.ok) {
            await fetchData();
          } else {
            const data = await res.json().catch(() => null);
            alert(data?.detail || `Reopen failed (HTTP ${res.status})`);
          }
        } catch (e) {
          alert(e.message || 'Network error');
        }
      },
    });
  };

  // Calculate totals
  const totalInStock = coils.filter(c => c.status === 'in_stock').reduce((sum, c) => sum + c.weightKg, 0);
  const totalShipping = coils.filter(c => c.status === 'shipping').reduce((sum, c) => sum + c.weightKg, 0);
  const totalProducedKg = productStock.reduce((sum, s) => {
    const w = parseFloat(s.productSpecs?.sheetWidth) || 0;
    const t = parseFloat(s.productSpecs?.thickness) || 0;
    const perProfile = (w / 1000) * 6 * (t * 7.85 + 0.1); // incl. Z100 zinc
    return sum + (s.count || 0) * perProfile;
  }, 0);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Warehouse className="w-6 h-6 text-blue-500" />
          <h1 className="text-xl sm:text-2xl font-bold text-white">Coil Stock</h1>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={runAudit}
            variant="outline"
            disabled={auditLoading}
            className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 gap-2"
            data-testid="audit-balance-btn"
          >
            <Scale className="w-4 h-4" />
            <span className="hidden sm:inline">{auditLoading ? 'Auditing…' : 'Audit Balance'}</span>
          </Button>
          <Button
            onClick={() => setShowImportModal(true)}
            variant="outline"
            className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10 gap-2"
            data-testid="import-po-btn"
          >
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">Import PO</span>
          </Button>
          <Button
            onClick={() => setShowAddForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Stock</span>
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-4">
          <div className="flex items-center gap-2 text-green-400 mb-1">
            <Package className="w-4 h-4" />
            <span className="text-sm">Raw Coils</span>
          </div>
          <div className="text-2xl font-bold text-white" data-testid="stat-raw-kg">{totalInStock.toLocaleString()} kg</div>
        </div>
        <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-4">
          <div className="flex items-center gap-2 text-amber-400 mb-1">
            <Ship className="w-4 h-4" />
            <span className="text-sm">Shipping</span>
          </div>
          <div className="text-2xl font-bold text-white" data-testid="stat-shipping-kg">{totalShipping.toLocaleString()} kg</div>
        </div>
        <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-4">
          <div className="flex items-center gap-2 text-purple-400 mb-1">
            <Layers className="w-4 h-4" />
            <span className="text-sm">Produced Stock</span>
          </div>
          <div className="text-2xl font-bold text-white" data-testid="stat-produced-kg">
            {totalProducedKg.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg
          </div>
        </div>
      </div>

      {/* Main Tabs: Coils vs Products */}
      <div className="flex gap-2 mb-4 border-b border-zinc-800 pb-2">
        <button
          onClick={() => setActiveTab('coils')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
            activeTab === 'coils' 
              ? 'bg-zinc-800 text-white border-b-2 border-blue-500' 
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          <Warehouse className="w-4 h-4 inline mr-2" />
          Raw Materials (Coils)
        </button>
        <button
          onClick={() => setActiveTab('products')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
            activeTab === 'products' 
              ? 'bg-zinc-800 text-white border-b-2 border-purple-500' 
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          <Layers className="w-4 h-4 inline mr-2" />
          Product Stock (6m profiles)
        </button>
      </div>

      {/* Coils Tab */}
      {activeTab === 'coils' && (
        <>
          {/* Filter Tabs */}
          <div className="flex gap-2 mb-4">
            {[
              { id: 'all', label: 'All' },
              { id: 'in_stock', label: 'In Stock' },
              { id: 'shipping', label: 'Shipping' },
              { id: 'closed', label: 'Closed' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === tab.id 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">
              {editingCoil ? 'Edit Entry' : 'Add Stock Entry'}
            </h2>
            <button onClick={resetForm} className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-4">
              <div>
                <Label className="text-zinc-400 text-sm">Sheet Width (mm)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.sheetWidth}
                  onChange={(e) => setFormData(prev => ({ ...prev, sheetWidth: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-200"
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Thickness (mm)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.thickness}
                  onChange={(e) => setFormData(prev => ({ ...prev, thickness: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-200"
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Zinc (g/m²)</Label>
                <Input
                  type="number"
                  step="1"
                  value={formData.coatingGsm}
                  onChange={(e) => setFormData(prev => ({ ...prev, coatingGsm: e.target.value }))}
                  placeholder="100"
                  className="bg-zinc-800 border-zinc-700 text-zinc-200"
                  data-testid="coil-coating-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Weight (kg)</Label>
                <Input
                  type="number"
                  step="1"
                  value={formData.weightKg}
                  onChange={(e) => setFormData(prev => ({ ...prev, weightKg: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-200"
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Cost</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-200"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Status</Label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-zinc-200"
                >
                  <option value="in_stock">In Stock</option>
                  <option value="shipping">Shipping</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-400 text-sm">Order Date</Label>
                <Input
                  type="date"
                  value={formData.orderDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, orderDate: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-200"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Arrival Date</Label>
                <Input
                  type="date"
                  value={formData.arrivalDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, arrivalDate: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-200"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-zinc-400 text-sm">Supplier</Label>
                <Input
                  type="text"
                  value={formData.supplier}
                  onChange={(e) => setFormData(prev => ({ ...prev, supplier: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-200"
                  placeholder="e.g., Gokmetal"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-400 text-sm">Invoice/PO Number</Label>
                <Input
                  type="text"
                  value={formData.invoiceNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-200"
                  placeholder="e.g., PI-0450"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Document URL</Label>
                <Input
                  type="text"
                  value={formData.documentUrl}
                  onChange={(e) => setFormData(prev => ({ ...prev, documentUrl: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-200"
                  placeholder="PDF link..."
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Notes</Label>
                <Input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-200"
                  placeholder="Quality, batch..."
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={resetForm} className="border-zinc-700 text-zinc-400">
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                {editingCoil ? 'Update' : 'Add'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Stock List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : Object.keys(groupedCoils).length === 0 ? (
        <div className="text-center py-12">
          <Warehouse className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500">No stock entries</p>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(groupedCoils)
            .sort((a, b) => a[1].sheetWidth - b[1].sheetWidth)
            .map(([key, group]) => {
              const matchingProducts = getMatchingProducts(group.sheetWidth, group.thickness);
              const totalProfiles = calculateProfiles(group.inStockWeight, group.sheetWidth, group.thickness);
              const avgLandedPerKg = group.landedWeightedKg > 0 ? group.landedWeightedSum / group.landedWeightedKg : 0;
              const pendingCount = group.pendingShipments.size;
              
              return (
                <div key={key} className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden">
                  {/* Dimension Header */}
                  <div className="p-4 border-b border-zinc-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-lg font-semibold text-white flex items-center gap-2">
                          {group.sheetWidth} × {group.thickness} mm
                          {pendingCount > 0 && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-amber-900/40 text-amber-300 inline-flex items-center gap-1" data-testid={`group-transport-pending-${key}`}>
                              <AlertTriangle className="w-3 h-3" /> Transport pending
                            </span>
                          )}
                        </div>
                        {matchingProducts.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {matchingProducts.map(p => (
                              <span key={p.id} className="text-xs px-2 py-0.5 bg-green-900/30 text-green-400 rounded">
                                {p.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        {/* Combined Stats */}
                        <div className="flex items-center gap-4 text-right">
                          {group.inStockWeight > 0 && (
                            <div>
                              <div className="text-lg font-bold text-green-400">{group.inStockWeight.toLocaleString()} kg</div>
                              <div className="text-xs text-zinc-500">~{totalProfiles.toLocaleString()} profiles</div>
                            </div>
                          )}
                          {group.shippingWeight > 0 && (
                            <div>
                              <div className="text-lg font-bold text-amber-400">{group.shippingWeight.toLocaleString()} kg</div>
                              <div className="text-xs text-zinc-500">shipping</div>
                            </div>
                          )}
                          {group.depletedWeight > 0 && (
                            <div>
                              <div className="text-lg font-bold text-rose-400">{group.depletedWeight.toLocaleString()} kg</div>
                              <div className="text-xs text-zinc-500">closed</div>
                            </div>
                          )}
                        </div>
                        {/* Cost & Supplier Summary */}
                        <div className="text-sm text-zinc-500 hidden sm:block">
                          {group.totalCost > 0 && <div>€{group.totalCost.toLocaleString()}</div>}
                          {avgLandedPerKg > 0 && (
                            <div className="text-emerald-300 text-xs" data-testid={`group-landed-${key}`}>€{avgLandedPerKg.toFixed(4)}/kg landed</div>
                          )}
                          {[...group.suppliers].length > 0 && <div>{[...group.suppliers].join(', ')}</div>}
                        </div>
                        {/* Documents */}
                        {group.documentUrls.length > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              // Append token so prod file URLs resolve via the
                              // middleware to the right DB on direct navigation.
                              let href = group.documentUrls[0];
                              if (!/^https?:/i.test(href)) href = `${API_URL}${href}`;
                              const t = localStorage.getItem('auth_token');
                              if (t && href.includes('/api/files/')) {
                                href += (href.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(t);
                              }
                              window.open(href, '_blank', 'noopener,noreferrer');
                            }}
                            className="text-blue-400 hover:text-blue-300"
                            title="View Document"
                            data-testid={`coil-doc-${key}`}
                          >
                            <FileText className="w-5 h-5" />
                          </button>
                        )}
                        {/* Expand Toggle */}
                        <button 
                          onClick={() => setExpandedGroup(expandedGroup === key ? null : key)}
                          className="p-1 text-zinc-400 hover:text-white"
                        >
                          {expandedGroup === key ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedGroup === key && (
                    <div className="border-t border-zinc-800 p-3 bg-zinc-800/30">
                      <div className="text-xs text-zinc-500 mb-2">
                        {group.coils.length} entries • Invoices: {[...group.invoices].join(', ') || 'N/A'}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {group.coils.map(coil => {
                          const info = coilLandedInfo(coil);
                          return (
                          <div key={coil.id} className="flex items-center gap-2 px-2 py-1 bg-zinc-800 rounded text-sm">
                            <span className={coil.status === 'in_stock' ? 'text-green-400' : coil.status === 'depleted' ? 'text-rose-400' : 'text-amber-400'}>
                              {(coil.status === 'depleted' ? (coil.scrappedWeightKg || 0) : coil.weightKg).toLocaleString()} kg
                            </span>
                            {coil.status === 'depleted' && (
                              <span className="text-[10px] uppercase tracking-wide text-rose-400 bg-rose-500/10 px-1.5 py-0.5 rounded" data-testid={`coil-closed-badge-${coil.id}`}>
                                closed
                              </span>
                            )}
                            {coil.cost && <span className="text-zinc-500">€{coil.cost}</span>}
                            {info.landedPerKg > 0 && (
                              <span className="text-emerald-300 text-[10px]" title="Landed €/kg (material + transport allocation)">
                                €{info.landedPerKg.toFixed(4)}/kg
                              </span>
                            )}
                            {info.transportPending && info.shipment && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setAttachShipment(info.shipment)}
                                className="h-6 px-1.5 text-amber-300 hover:text-amber-200 hover:bg-amber-500/10 text-[10px] gap-1"
                                title="Attach transport invoice for this shipment"
                                data-testid={`coil-attach-transport-${coil.id}`}
                              >
                                <Paperclip className="w-3 h-3" /> Transport
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEdit(coil)}
                              className="h-6 w-6 p-0 text-zinc-400 hover:text-white"
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                            {coil.status === 'in_stock' && coil.weightKg > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCloseOut(coil)}
                                className="h-6 px-2 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 text-[10px] uppercase tracking-wide"
                                title="Close this coil — remaining weight is scrapped"
                                data-testid={`coil-close-${coil.id}`}
                              >
                                Close
                              </Button>
                            )}
                            {coil.status === 'depleted' && (coil.scrappedWeightKg || 0) > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReopen(coil)}
                                className="h-6 px-2 text-green-400 hover:text-green-300 hover:bg-green-500/10 text-[10px] uppercase tracking-wide"
                                title="Reopen this coil — scrapped weight returns to stock"
                                data-testid={`coil-reopen-${coil.id}`}
                              >
                                Reopen
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(coil.id)}
                              className="h-6 w-6 p-0 text-zinc-400 hover:text-red-400"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
        </>
      )}

      {/* Product Stock Tab */}
      {activeTab === 'products' && (() => {
        const visibleStock = productStock.filter(s => (s.count || 0) > 0);
        // Helper to open the backdate dialog with a sensible default
        const openEditLog = (log) => {
          setEditLog(log);
          setEditLogDate((log.producedAt || '').slice(0, 10));
          setEditLogError(null);
        };
        return (
        <div className="space-y-6">
          {visibleStock.length === 0 ? (
            <div className="text-center py-12">
              <Layers className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">No product stock yet</p>
              <p className="text-sm text-zinc-600 mt-1">Use Factory to produce profiles from coils</p>
            </div>
          ) : (
            <div className="space-y-3">
              {visibleStock.map(stock => (
                <div key={stock.id} className="bg-zinc-900 rounded-lg border border-zinc-800 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-lg font-semibold text-white">
                        {stock.productLabel}
                        {disp?.variantNoteStock && products.find(p => p.id === stock.productId)?.variantNote ? (
                          <span className="ml-2 text-xs text-zinc-500 font-normal">({products.find(p => p.id === stock.productId).variantNote})</span>
                        ) : null}
                      </div>
                      {stock.productSpecs && (
                        <div className="text-sm text-zinc-500">
                          {parseFloat(stock.productSpecs.sheetWidth).toFixed(1)}mm × {stock.productSpecs.thickness}mm
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-2xl font-bold text-purple-400">{stock.count}</div>
                        <div className="text-xs text-zinc-500">6m profiles</div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setReturnStock(stock); setReturnQty(''); setReturnError(null); }}
                        className="h-7 text-xs border-amber-800 text-amber-300 hover:bg-amber-950/40 hover:text-amber-200"
                        data-testid={`return-to-coil-${stock.productId}`}
                      >
                        Return to coil
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Production History — lets the operator backdate historical
              production runs that were entered with today's date. */}
          {productionLogs.length > 0 && (
            <div className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden" data-testid="production-history-panel">
              <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-zinc-200">Production History</div>
                  <div className="text-xs text-zinc-500">{productionLogs.length} run{productionLogs.length === 1 ? '' : 's'} · edit dates to backfill history, or revert an accidental run</div>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs text-zinc-500 uppercase border-b border-zinc-800">
                  <tr>
                    <th className="text-left px-4 py-2">Product</th>
                    <th className="text-center px-2 py-2">Qty</th>
                    <th className="text-center px-2 py-2">kg used</th>
                    <th className="text-center px-2 py-2">Source coil</th>
                    <th className="text-center px-2 py-2">Produced</th>
                    <th className="text-right px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {productionLogs.map(log => (
                    <tr key={log.id} className="border-b border-zinc-800/50 last:border-0">
                      <td className="px-4 py-2 text-zinc-200">{log.productLabel}</td>
                      <td className="text-center px-2 py-2 text-zinc-300">{log.quantityProduced}</td>
                      <td className="text-center px-2 py-2 text-zinc-400">{log.weightUsedKg?.toFixed?.(2) ?? log.weightUsedKg}</td>
                      <td className="text-center px-2 py-2 text-zinc-500 text-xs" data-testid={`log-source-coil-${log.id}`}>
                        {(() => {
                          const c = coils.find(x => x.id === log.coilStockId);
                          return c ? `${c.sheetWidth}×${c.thickness}mm` : '—';
                        })()}
                      </td>
                      <td className="text-center px-2 py-2 text-zinc-400 text-xs">{(log.producedAt || '').slice(0, 10)}</td>
                      <td className="text-right px-4 py-2">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditLog(log)}
                            className="h-7 text-xs border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                            data-testid={`edit-log-date-${log.id}`}
                          >
                            Edit date
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setReassignLog(log); setReassignCoilId(''); setReassignError(null); }}
                            className="h-7 text-xs border-sky-800 text-sky-300 hover:bg-sky-950/40 hover:text-sky-200"
                            data-testid={`reassign-log-${log.id}`}
                          >
                            Move coil
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setConfirmDialog({
                              title: 'Revert this production run?',
                              description: `Undo production of ${log.quantityProduced} × ${log.productLabel}. This will restore ${log.weightUsedKg?.toFixed?.(2) ?? log.weightUsedKg} kg to the source coil, remove the profiles from product stock, and delete the FIFO batch. Blocked if any invoice already consumed from this batch.`,
                              confirmLabel: 'Revert',
                              confirmVariant: 'destructive',
                              onConfirm: async () => {
                                try {
                                  const token = localStorage.getItem('auth_token');
                                  const r = await fetch(`${API_URL}/api/production-logs/${log.id}/revert`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
                                  });
                                  if (r.ok) {
                                    await fetchData();
                                  } else {
                                    const e = await r.json().catch(() => ({}));
                                    const d = e?.detail;
                                    const msg = (d && typeof d === 'object') ? (d.message || JSON.stringify(d)) : (d || `HTTP ${r.status}`);
                                    alert(msg);
                                  }
                                } catch (err) {
                                  alert(err.message || 'Network error');
                                }
                              },
                            })}
                            className="h-7 text-xs border-rose-800 text-rose-300 hover:bg-rose-950/40 hover:text-rose-200"
                            data-testid={`revert-log-${log.id}`}
                          >
                            Revert
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        );
      })()}

      <PurchaseOrderImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onCoilsCreated={(created) => {
          setCoils(prev => [...prev, ...created]);
          // Re-fetch shipments since a new one was just created alongside the coils
          fetchData();
        }}
      />

      <AttachTransportInvoiceModal
        open={!!attachShipment}
        shipment={attachShipment}
        onClose={() => setAttachShipment(null)}
        onSaved={() => { setAttachShipment(null); fetchData(); }}
      />

      {/* Production-log date edit dialog */}
      <AlertDialog open={!!editLog} onOpenChange={(o) => { if (!o) { setEditLog(null); setEditLogError(null); } }}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800" data-testid="edit-log-date-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Backdate production</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Change when <strong className="text-zinc-200">{editLog?.productLabel}</strong> ({editLog?.quantityProduced} pcs)
              was produced. This updates the production log AND every FIFO batch it created. New date can&apos;t predate
              the coil&apos;s arrival or be later than any invoice that already consumed from this batch.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2 space-y-2">
            <Label className="text-zinc-300 text-xs">Production date</Label>
            <Input
              type="date"
              value={editLogDate}
              onChange={(e) => setEditLogDate(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              className="bg-zinc-800 border-zinc-700 text-zinc-100"
              data-testid="edit-log-date-input"
            />
            {editLogError && (
              <div className="text-xs text-red-400 bg-red-950/30 border border-red-900/40 rounded p-2 whitespace-pre-line" data-testid="edit-log-date-error">
                {editLogError}
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700" disabled={editLogSaving}>
              Cancel
            </AlertDialogCancel>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={editLogSaving || !editLogDate}
              onClick={async () => {
                setEditLogError(null);
                setEditLogSaving(true);
                try {
                  const token = localStorage.getItem('auth_token');
                  const r = await fetch(`${API_URL}/api/production-logs/${editLog.id}/produced-at`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
                    body: JSON.stringify({ effectiveAt: editLogDate }),
                  });
                  if (r.ok) {
                    setEditLog(null);
                    fetchData();
                  } else {
                    const e = await r.json().catch(() => ({}));
                    const d = e?.detail;
                    if (d && typeof d === 'object') setEditLogError(d.message || JSON.stringify(d));
                    else setEditLogError(d || `HTTP ${r.status}`);
                  }
                } catch (err) {
                  setEditLogError(err.message || 'Network error');
                } finally {
                  setEditLogSaving(false);
                }
              }}
              data-testid="edit-log-date-confirm"
            >
              {editLogSaving ? 'Saving…' : 'Save date'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reassign a production run to the coil that was really used */}
      <AlertDialog open={!!reassignLog} onOpenChange={(o) => { if (!o) { setReassignLog(null); setReassignError(null); } }}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800" data-testid="reassign-coil-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Move run to another coil</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {reassignLog && (() => {
                const cur = coils.find(x => x.id === reassignLog.coilStockId);
                return (
                  <>Run of <strong className="text-zinc-200">{reassignLog.quantityProduced} × {reassignLog.productLabel}</strong> on {(reassignLog.producedAt || '').slice(0, 10)} currently charged to
                  {' '}<strong className="text-zinc-200">{cur ? `${cur.sheetWidth}×${cur.thickness}mm (${cur.supplier || cur.invoiceNumber || 'coil'})` : 'unknown coil'}</strong>.
                  Moving it returns <strong className="text-zinc-200">{reassignLog.weightUsedKg?.toFixed?.(2)} kg</strong> to that coil and takes it from the one you pick (if the target is already closed, its recorded scrap shrinks instead — nothing is counted twice). Costs and invoices are not changed.</>
                );
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2 space-y-2">
            <Label className="text-zinc-300 text-xs">Coil that was really used</Label>
            <select
              value={reassignCoilId}
              onChange={(e) => setReassignCoilId(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 text-zinc-100 rounded-md h-9 px-2 text-sm"
              data-testid="reassign-coil-select"
            >
              <option value="">— select coil —</option>
              {coils.filter(c => c.id !== reassignLog?.coilStockId).map(c => (
                <option key={c.id} value={c.id}>
                  {c.sheetWidth}×{c.thickness}mm · {c.status === 'depleted' ? `closed (${c.scrappedWeightKg || 0}kg scrap)` : `${c.weightKg}kg`} · {c.supplier || c.invoiceNumber || c.id.slice(0, 8)}
                </option>
              ))}
            </select>
            {reassignError && (
              <div className="text-xs text-red-400 bg-red-950/30 border border-red-900/40 rounded p-2" data-testid="reassign-coil-error">
                {reassignError}
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700" disabled={reassignSaving}>Cancel</AlertDialogCancel>
            <Button
              className="bg-sky-600 hover:bg-sky-700 text-white"
              disabled={reassignSaving || !reassignCoilId}
              onClick={async () => {
                setReassignError(null);
                setReassignSaving(true);
                try {
                  const token = localStorage.getItem('auth_token');
                  const r = await fetch(`${API_URL}/api/production-logs/${reassignLog.id}/reassign-coil`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
                    body: JSON.stringify({ newCoilId: reassignCoilId }),
                  });
                  if (r.ok) {
                    setReassignLog(null);
                    fetchData();
                  } else {
                    const e = await r.json().catch(() => ({}));
                    const d = e?.detail;
                    setReassignError((d && typeof d === 'object') ? (d.message || JSON.stringify(d)) : (d || `HTTP ${r.status}`));
                  }
                } catch (err) {
                  setReassignError(err.message || 'Network error');
                } finally {
                  setReassignSaving(false);
                }
              }}
              data-testid="reassign-coil-confirm"
            >
              {reassignSaving ? 'Moving…' : 'Move run'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Coil balance audit results */}
      <AlertDialog open={!!auditResult} onOpenChange={(o) => { if (!o) setAuditResult(null); }}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl" data-testid="audit-result-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Coil balance audit</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Checks every coil: original weight = current + produced + scrapped. A mismatch means kg
              changed without a matching production or waste log (or the weight was edited manually).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2 max-h-80 overflow-y-auto">
            {auditResult?.mismatchCount === 0 ? (
              <div className="text-emerald-400 text-sm bg-emerald-950/30 border border-emerald-900/40 rounded p-3" data-testid="audit-all-clear">
                ✓ All {auditResult.coilsChecked} coils balance — no unexplained weight changes.
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-zinc-500">{auditResult?.coilsChecked} coils checked · {auditResult?.mismatchCount} mismatch(es)</div>
                {auditResult?.mismatches?.map((m) => (
                  <div key={m.coilId} className="bg-zinc-800/50 border border-amber-900/40 rounded p-2 text-xs" data-testid={`audit-mismatch-${m.coilId}`}>
                    <div className="text-zinc-200 font-medium">{m.label} <span className="text-zinc-500">({m.status})</span></div>
                    <div className="text-zinc-400 mt-1">
                      Original {m.originalWeightKg}kg − produced {m.producedKg}kg − scrapped {m.scrappedKg}kg
                      = expected <strong className="text-zinc-200">{m.expectedWeightKg}kg</strong>,
                      but stored weight is <strong className="text-zinc-200">{m.actualWeightKg}kg</strong>
                    </div>
                    <div className={`mt-1 font-semibold ${m.deltaKg < 0 ? 'text-red-400' : 'text-amber-400'}`}>
                      {m.deltaKg < 0 ? `${Math.abs(m.deltaKg)}kg missing without a log` : `${m.deltaKg}kg extra vs logs`}
                      {' — '}fix via Edit → Weight = {m.expectedWeightKg}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700">Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Return-to-coil dialog: partial revert of UNSOLD finished stock */}
      <AlertDialog open={!!returnStock} onOpenChange={(o) => { if (!o) { setReturnStock(null); setReturnError(null); } }}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800" data-testid="return-to-coil-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Return stock to coil</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Send unsold <strong className="text-zinc-200">{returnStock?.productLabel}</strong> pieces
              (currently {returnStock?.count} in stock) back to raw coil stock. Newest-produced pieces go back
              first and each portion&apos;s kg returns to its original source coil. Sold pieces are never touched —
              if you ask for more than the unsold amount, nothing happens and you&apos;ll see the available count.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2 space-y-2">
            <Label className="text-zinc-300 text-xs">Quantity to return</Label>
            <Input
              type="number"
              min="1"
              value={returnQty}
              onChange={(e) => setReturnQty(e.target.value)}
              placeholder="e.g. 42"
              className="bg-zinc-800 border-zinc-700 text-zinc-100"
              data-testid="return-to-coil-qty-input"
            />
            {returnError && (
              <div className="text-xs text-red-400 bg-red-950/30 border border-red-900/40 rounded p-2 whitespace-pre-line" data-testid="return-to-coil-error">
                {returnError}
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700" disabled={returnSaving}>
              Cancel
            </AlertDialogCancel>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              disabled={returnSaving || !returnQty || parseInt(returnQty, 10) <= 0}
              onClick={async () => {
                setReturnError(null);
                setReturnSaving(true);
                try {
                  const token = localStorage.getItem('auth_token');
                  const r = await fetch(`${API_URL}/api/product-stock/return-to-coil`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
                    body: JSON.stringify({ productId: returnStock.productId, quantity: parseInt(returnQty, 10) }),
                  });
                  if (r.ok) {
                    setReturnStock(null);
                    fetchData();
                  } else {
                    const e = await r.json().catch(() => ({}));
                    const d = e?.detail;
                    setReturnError((d && typeof d === 'object') ? (d.message || JSON.stringify(d)) : (d || `HTTP ${r.status}`));
                  }
                } catch (err) {
                  setReturnError(err.message || 'Network error');
                } finally {
                  setReturnSaving(false);
                }
              }}
              data-testid="return-to-coil-confirm"
            >
              {returnSaving ? 'Returning…' : 'Return to coil'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* In-app confirm dialog (replaces window.confirm) */}
      <AlertDialog open={!!confirmDialog} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800" data-testid="stock-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{confirmDialog?.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {confirmDialog?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700 hover:text-white">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className={
                confirmDialog?.confirmVariant === 'destructive'
                  ? 'bg-rose-600 hover:bg-rose-700 text-white'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }
              onClick={async () => {
                const fn = confirmDialog?.onConfirm;
                setConfirmDialog(null);
                if (fn) await fn();
              }}
            >
              {confirmDialog?.confirmLabel || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StockPage;
